import { BROWSER_STORAGE_BASE_NAME, DOC_FORMAT_CONFIG_VALUES } from '../define/global';
import { NAVERWORKS_DRIVE_TYPE } from '../define/naverworksDefine';
import { cLog } from './consoleLog';
import { getExtension } from './FileControlUtil';
import { CHANGE_FONT_TEMP_FILE_EXT } from './FontsUtil';

class IndexedDBManager {
  constructor() {
    this.name = BROWSER_STORAGE_BASE_NAME;
    this.version = '1';
    this.storeName = 'RecentFiles';

    this.DB = null;

    this.testName = `TEST_${BROWSER_STORAGE_BASE_NAME}`;
  }

  async available() {
    const createDummyFile = () => {
      const blob = new Blob(['test'], { type: 'text/plain;charset=utf-8' });
      const file = new File([blob], 'test.txt', { type: 'text/plain' });
      return file;
    };
    const testFile = createDummyFile();

    const deleteDB = () => {
      return new Promise((resolve, reject) => {
        try {
          const request = window.indexedDB.deleteDatabase(this.testName);
          request.onerror = () => {
            cLog('Error deleting database');
            reject();
          };
          request.onsuccess = () => {
            resolve(true);
          };
        } catch (e) {
          cLog(`Exception deleting database : ${e}`);
          reject();
        }
      });
    };

    try {
      await this.open(this.testName, this.version);
      await this.saveFile(testFile);
      const dummy = await this.getFile(testFile.name);
      if (!dummy) {
        return false;
      }
      await this.DB.close();
      this.DB = null;
      return await deleteDB();
    } catch (e) {
      return false;
    }
  }

  open(name = this.name, version = this.version) {
    return new Promise((resolve, reject) => {
      const openRequest = window.indexedDB.open(name, version);
      openRequest.onerror = () => {
        cLog('indexedDB open : onerror');
        reject();
      };
      openRequest.onblocked = () => {
        cLog('indexedDB open : onblocked');
      };
      openRequest.onupgradeneeded = (event) => {
        this.DB = event.target.result;
        if (!this.DB) {
          cLog('DB is null');
        }

        try {
          this.DB.createObjectStore(this.storeName, {
            keyPath: 'file.name',
            autoIncrement: false
          });
        } catch (err) {
          cLog('Error in openDB_onupgradeneeded');
          reject();
        }
      };
      openRequest.onsuccess = (event) => {
        this.DB = event.target.result;
        resolve();
      };
    });
  }

  async getFiles() {
    try {
      const transaction = this.DB.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const getFileList = () => {
        return new Promise((resolve, reject) => {
          const cursorRequest = store.openCursor();
          cursorRequest.onerror = () => {
            cLog('getFileList cursor.onerror');
            reject();
          };

          const fileList = [];
          cursorRequest.onsuccess = (event) => {
            const cursor = event.target.result;
            if (cursor) {
              const filename = cursor.key;
              const ext = getExtension(filename).toLowerCase();
              const extTmp = CHANGE_FONT_TEMP_FILE_EXT.substring(1);
              if (DOC_FORMAT_CONFIG_VALUES.extList.includes(ext) || ext === extTmp) {
                fileList.push(cursor.value);
              }
              cursor.continue();
            } else {
              resolve(fileList);
            }
          };
        });
      };
      const fileList = await getFileList();
      const sortedFiles = fileList.sort((l, r) => {
        return new Date(r.updateTime) - new Date(l.updateTime);
      });
      return sortedFiles;
    } catch (err) {
      cLog('IndexedDBManager getFiles error');
      return null;
    }
  }

  _delegator(file, operator) {
    return new Promise((resolve, reject) => {
      try {
        const transaction = this.DB.transaction([this.storeName], 'readwrite');
        const store = transaction.objectStore(this.storeName);
        const request = operator(store, file);
        request.onsuccess = () => {
          resolve();
        };
        request.onerror = (event) => {
          cLog(event);
          reject();
        };
      } catch (exception) {
        cLog(exception);
        reject();
      }
    });
  }

  saveFile(file, isNaverWorksFile, naverWorksFileInfo) {
    return new Promise((resolve, reject) => {
      this._delegator(file, (store, file) => {
        let fileInfo = {
          file: file,
          updateTime: Date.now()
        };

        if (isNaverWorksFile && naverWorksFileInfo) {
          fileInfo = {
            ...fileInfo,
            isNaverWorksFile,
            naverWorksFileInfo: {
              ...naverWorksFileInfo,
              driveType: naverWorksFileInfo.driveType || NAVERWORKS_DRIVE_TYPE.PERSONAL,
              driveId: naverWorksFileInfo.driveId || 'me'
            }
          };
        }

        return store.put(fileInfo);
      })
        .then(resolve)
        .catch(reject);
    });
  }

  removeFile(file) {
    return new Promise((resolve, reject) => {
      return this._delegator(file, (store, file) => store.delete(file.name))
        .then(resolve)
        .catch(reject);
    });
  }

  getFile(filename) {
    return new Promise((resolve, reject) => {
      try {
        const stransaction = this.DB.transaction(this.storeName, 'readonly');
        const store = stransaction.objectStore(this.storeName);
        const request = store.get(filename);
        request.onsuccess = () => {
          resolve(request.result);
        };
      } catch (err) {
        cLog('indexedDb getFile Error' + err.message);
        reject();
      }
    });
  }
}

export default IndexedDBManager;
